import React, { Suspense, createContext, useEffect, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import * as Sentry from '@sentry/browser';
import ReactGA from 'react-ga';
import Rollbar from 'rollbar';
import i18n from './i18n';
import { useTranslation } from 'react-i18next';
import { ConfigManager, MessagesServiceProvider as LegoServiceProvider } from 'lego-dist';

import './App.css';
import Routes from './routes';
import SplashScreen from './components/shared/SplashScreen';
import EventReporter, { getEventProviderInstance } from './appEvents/index';
import { EventProviders } from 'constants/events';
import { ColorPalettes } from 'kinedu-react-components';

import useAccount from './hooks/useAccount';
import useSubscription from './hooks/UseSubscription';

// import { SUBSCRIPTION_TYPES } from './constants/general';
import LoggerContainer from './components/shared/LoggerContainer';
import { APP_VERSION, BUILD_NAME, ENV, GA_ID, ROLLBAR_TOKEN, ENVIRONMENT, IS_PRODUCTION, AWS_KEY, AWS_REGION, FILES_API_URL, S3_BUCKET_FILES } from './config';
import { KEYS_LOCALSTORAGE } from 'constants/general';
import themes from 'themes';
import { mixpanel } from 'appEvents/providers';
import NotificationBanner from './components/shared/NotificationBanner';
import { s3Config } from 'settings/filer/s3';
import { messages } from 'settings/messages';
import { ROLES_OBJECT } from "constants/onBoarding"
import { isRoleType } from 'lib/helpers';
import { APP_SECTIONS_PATHS } from 'constants/routes';
import ReportButton from 'components/shared/ReportButton';
import { ActionPermission } from 'lib/specialPermissions';
import useAppcues from 'hooks/useAppcues';
import useOrganization from 'hooks/UseOrganization';
import { LOCAL_CENTER_ID } from 'constants/localStorage';

// Initialize and set Pageview for Google Analytics
ReactGA.initialize(GA_ID);
ReactGA.pageview(window.location.pathname + window.location.search);

export const AppContext = createContext<any>(null);

const { KineduPalette } = ColorPalettes;
const StyledApp = styled.div`
  background-color: ${KineduPalette.shadeLighter};
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
  z-index: 1;
`;

const partnership = localStorage.getItem('partnership') || '';
const token = localStorage.getItem('auth_token') || '';
const userIdentified = localStorage.getItem('user_identified');
const userLocale = localStorage.getItem(KEYS_LOCALSTORAGE.USER_LOCALE) || '';

if (!userIdentified) localStorage.setItem('user_identified', 'true');
i18n.changeLanguage(userLocale);

/* Register global configurations */
ConfigManager.registerConfig('i18n', {
  i18n,
  useTranslation,
});
ConfigManager.registerConfig('S3Config', s3Config);
ConfigManager.registerConfig('messages', messages);
ConfigManager.registerConfig('ENVConfig', ENVIRONMENT);
ConfigManager.registerConfig('ROLES', ROLES_OBJECT);
ConfigManager.registerConfig('isRoleType', isRoleType);
ConfigManager.registerConfig('KEYS_LOCALSTORAGE', KEYS_LOCALSTORAGE);
ConfigManager.registerConfig('APP_SECTIONS_PATHS', APP_SECTIONS_PATHS);
ConfigManager.registerConfig('ActionPermission', ActionPermission);

const RollbarInstance = new Rollbar({
  accessToken: ROLLBAR_TOKEN,
  captureUncaught: true,
  captureUnhandledRejections: true,
  payload: {
    environment: ENV,
    code_version: APP_VERSION,
    server: {
      branch: 'master',
      root: '/',
    },
  },
});


/** -----------------------  Temporal workaround ------------------------- */

window.ConfigurationManager = ConfigManager;

declare global {
  interface Window {
    ConfigurationManager: any;
  }
}

/** -----------------------  Temporal workaround ------------------------- */
// Esto para cuando no es producción

function App() {
  const {
    activePlan,
    availablePlans,
    subscription,
    fetchSubscription,
    getPremiumPlans,
    isSubscriptionCancel,
    isDemoUser,
    isFreemiumUser,
    isPremium,
  } = useSubscription();
  const { user } = useAccount();
  const { appcuesService } = useAppcues();
  const [showBanner, setShowBanner] = useState(!IS_PRODUCTION);
  const { organization } = useOrganization();

  if (!userIdentified) {
    localStorage.setItem('user_identified', 'true');
  }

  useEffect(() => {
    if (user) {
      i18n.changeLanguage(
        user?.locale ||
          localStorage.getItem(KEYS_LOCALSTORAGE.USER_LOCALE) ||
          'es',
      );
    }
    if (user.id) {
      getEventProviderInstance(EventProviders.MIXPANEL).identify(
        user.id?.toString(),
      );

      EventReporter.setUserAttributes({
        ...user,
        partnership,
        organization_name: localStorage.getItem(KEYS_LOCALSTORAGE.ORGANIZATION_NAME),
      });

      mixpanel.getInstance().register({
        organization_name: localStorage.getItem(KEYS_LOCALSTORAGE.ORGANIZATION_NAME),
      });

      // identify user in appCuest
      appcuesService.identify((user.id, {
        id: user.id,
        name: user.name,
        last_name: user.lastname,
        email: user.email,
        role: user.type,
        organization: organization?.id,
        center: LOCAL_CENTER_ID(),
        classroom: localStorage.getItem(KEYS_LOCALSTORAGE.CLASSROOM_ID),
        language: localStorage.getItem(KEYS_LOCALSTORAGE.USER_LOCALE),
      }));

      Sentry.configureScope((scope) => {
        scope.setUser({
          id: user.id?.toString(),
          email: user.email,
          username: user.fullname,
          role: user.type,
        });
      });

      RollbarInstance.configure({
        payload: {
          person: {
            id: user.id,
            username: user.fullname,
            email: user.email,
          }
        }
      });
    }
    /* eslint-disable-next-line */
  }, [user]);

  useEffect(() => {
    getPremiumPlans();
    if (token) {
      fetchSubscription();
    }
    /* eslint-disable-next-line */
  }, []);

  const context = {
    user,
    activePlan,
    availablePlans,
    isDemoUser,
    isFreemiumUser: isFreemiumUser || isDemoUser,
    isPremium,
    billingPeriodEnd: subscription?.billingPeriodEnd,
    subscription,
    isSubscriptionCancel,
  };

  const messagesDependencies = {};
  const awsService = {
    bucket: S3_BUCKET_FILES,
    apiURL: FILES_API_URL,
    region: AWS_REGION,
    key: AWS_KEY,
  };

  const services = {
    aws: awsService,
  };

  return (
    <AppContext.Provider value={context}>
      <Suspense fallback={<SplashScreen loading />}>
        <ThemeProvider theme={themes}>
          <StyledApp className="App">
            <NotificationBanner
              show={showBanner}
              message={`STAGING v${APP_VERSION} - ${BUILD_NAME}`}
              buttonMessage="Close"
              buttonCallback={() => setShowBanner(false)}
            />
            <ReportButton />
            <LoggerContainer user={user} isPremium={isPremium} />
            <LegoServiceProvider
              dependencies={messagesDependencies}
              constants={{ userId: user.id, isFreemiumUser }}
              services={services}
            >
              <Routes />
            </LegoServiceProvider>
          </StyledApp>
        </ThemeProvider>
      </Suspense>
    </AppContext.Provider>
  );
}

export default App;
